import { useEffect, useState, useRef } from "react";
import api, { formatErr, API } from "@/lib/api";
import { Upload, Search, Trash2, Loader2, CheckCircle2, Download, Users, Upload as UploadIcon, Star, Scissors } from "lucide-react";

export default function Recipients() {
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("");
  const [uploadGroup, setUploadGroup] = useState("newsletter");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [drag, setDrag] = useState(false);
  const fileInput = useRef(null);

  const PAGE_SIZE = 200;
  const [loadingMore, setLoadingMore] = useState(false);

  const load = async () => {
    const [s, r, g] = await Promise.all([
      api.get("/recipients/stats"),
      api.get("/recipients", { params: { q, filter, group: groupFilter, skip: 0, limit: PAGE_SIZE } }),
      api.get("/recipients/groups"),
    ]);
    setStats(s.data);
    setItems(r.data.items);
    setTotal(r.data.total);
    setGroups(g.data.items || []);
  };
  useEffect(() => { load(); }, [q, filter, groupFilter]);

  const loadMore = async () => {
    if (loadingMore || items.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/recipients", {
        params: { q, filter, group: groupFilter, skip: items.length, limit: PAGE_SIZE },
      });
      setItems(prev => [...prev, ...(data.items || [])]);
      setTotal(data.total);
    } finally {
      setLoadingMore(false);
    }
  };

  // Confirmation gate — we never upload without a clear, explicit tag confirmation
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingTags, setPendingTags] = useState("donors");
  const [pendingMode, setPendingMode] = useState("add"); // 'add' | 'remove'

  const doUpload = async () => {
    if (!pendingFile) return;
    const file = pendingFile;
    const tagsCSV = (pendingTags || "").split(",").map(t => t.trim()).filter(Boolean).join(",");
    if (!tagsCSV) { alert("Type at least one tag (e.g. staff, donors)"); return; }
    setPendingFile(null);
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const endpoint = pendingMode === "remove" ? "/recipients/untag" : "/recipients/upload";
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { groups: tagsCSV },
      });
      setUploadResult({ ...data, mode: pendingMode });
      await load();
    } catch (e) {
      setUploadResult({ error: formatErr(e.response?.data?.detail) });
    } finally { setUploading(false); }
  };

  const upload = (file) => {
    // Open the confirmation modal pre-filled with the currently-selected upload tag
    setPendingFile(file);
    setPendingTags(uploadGroup || "donors");
    setPendingMode("add");
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const removeOne = async (id) => {
    if (!window.confirm("Remove this recipient?")) return;
    await api.delete(`/recipients/${id}`);
    load();
  };

  const downloadActive = () => {
    const token = localStorage.getItem("rahi_token");
    const params = new URLSearchParams({ filter: "active" });
    if (groupFilter) params.set("group", groupFilter);
    fetch(`${API}/recipients/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = `rahi_recipients_active${groupFilter ? "_"+groupFilter : ""}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      });
  };

  const downloadBackup = async () => {
    if (!window.confirm("Download a full app backup (recipients + all campaigns + groups) as a ZIP? Recommended before shutting down the deployment.")) return;
    const token = localStorage.getItem("rahi_token");
    const resp = await fetch(`${API}/admin/backup`, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) { alert("Backup failed: HTTP " + resp.status); return; }
    const blob = await resp.blob();
    const a = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    const stamp = new Date().toISOString().slice(0,10);
    a.download = `rahi_mailer_backup_${stamp}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const restoreInput = useRef(null);
  const [restoring, setRestoring] = useState(false);
  const restoreFromBackup = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      alert("Please select a backup .zip file."); return;
    }
    if (!window.confirm(`Restore from "${file.name}"?\n\nThis ADDS recipients & campaigns from the backup. Existing recipients KEEP their tags and bounce/unsub state. Existing campaigns (same id) are SKIPPED — not overwritten.`)) return;
    setRestoring(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s = data.summary || {};
      const r = s.recipients || {};
      const c = s.campaigns || {};
      const lines = [
        `Restored from backup${s.version_in_zip ? ` (${s.version_in_zip})` : ""}:`,
        `• Recipients — processed: ${r.processed||0}, added: ${r.added||0}, retagged: ${r.tagged||0}, marked inactive: ${r.marked_inactive||0}`,
        `• Campaigns — in zip: ${c.in_zip||0}, added: ${c.added||0}, skipped (already exists): ${c.skipped_existing||0}`,
      ];
      if ((s.warnings || []).length) lines.push("Warnings:\n  - " + s.warnings.join("\n  - "));
      alert(lines.join("\n"));
      await load();
    } catch (e) {
      alert("Restore failed: " + formatErr(e.response?.data?.detail));
    } finally { setRestoring(false); }
  };

  return (
    <div className="px-12 py-10 max-w-[1200px]" data-testid="recipients-page">
      <div className="mb-12 flex items-end justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--rahi-red)" }}>Your mailing list</p>
          <h1 className="font-display text-5xl font-black tracking-tight leading-none">Recipients</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--rahi-muted)" }}>
            Upload your list as CSV. We will dedupe by email and skip anyone who has unsubscribed or bounced.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={downloadActive} className="btn btn-ghost" data-testid="download-active-btn">
            <Download size={14}/> Download active list
          </button>
          <button onClick={downloadBackup} className="btn btn-ghost" data-testid="download-backup-btn" title="Download full backup ZIP — recommended before pausing the deployment">
            <Download size={14}/> Backup all (zip)
          </button>
          <input
            ref={restoreInput} type="file" accept=".zip" className="hidden"
            onChange={(e) => e.target.files?.[0] && restoreFromBackup(e.target.files[0])}
            data-testid="restore-file-input"
          />
          <button
            onClick={() => restoreInput.current?.click()}
            disabled={restoring}
            className="btn btn-ghost"
            data-testid="restore-backup-btn"
            title="Restore recipients & campaigns from a previously downloaded backup ZIP"
          >
            {restoring ? <Loader2 size={14} className="animate-spin"/> : <UploadIcon size={14}/>}
            {restoring ? "Restoring…" : "Restore from zip"}
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-10">
          <StatTile label="Total" value={stats.total} testid="stat-total"/>
          <StatTile label="Active" value={stats.active} accent testid="stat-active"/>
          <StatTile label="Unsubscribed" value={stats.unsubscribed} testid="stat-unsub"/>
          <StatTile label="Bounced" value={stats.bounced} testid="stat-bounced"/>
        </div>
      )}

      {/* Group selector for upload */}
      <div className="mb-4 flex items-center gap-3" data-testid="upload-group-row">
        <span className="text-xs uppercase tracking-[0.14em]" style={{ color:"var(--rahi-soft)" }}>Tag uploads as</span>
        <input
          value={uploadGroup} onChange={(e)=>setUploadGroup(e.target.value)}
          placeholder="donors, volunteers"
          list="known-groups"
          className="input"
          style={{ width: 320 }}
          data-testid="upload-group-input"
        />
        <datalist id="known-groups">
          {groups.filter(g => !(g.virtual === true || g.group === "master")).map(g => <option key={g.group} value={g.group}/>)}
        </datalist>
        <span className="text-xs" style={{ color:"var(--rahi-soft)" }}>
          Comma-separate to apply multiple tags. Existing emails keep prior tags.
        </span>
      </div>

      {/* Upload */}
      <div
        className={`dropzone mb-8 ${drag ? "drag-over" : ""}`}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid="csv-dropzone"
      >
        <input
          ref={fileInput} type="file" accept=".csv" className="hidden"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          data-testid="csv-file-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <Loader2 size={16} className="animate-spin"/> Importing into <span className="font-mono">{uploadGroup}</span>…
          </div>
        ) : (
          <>
            <Upload size={22} className="mx-auto mb-3" style={{ color: "var(--rahi-soft)" }}/>
            <div className="font-medium text-sm">Drop CSV here or click to upload</div>
            <div className="text-xs mt-1" style={{ color: "var(--rahi-soft)" }}>
              Required column: <span className="font-mono">email</span> &middot; Optional: <span className="font-mono">name</span> &middot; Group: <span className="font-mono">{uploadGroup || "donors"}</span>
            </div>
            <div className="text-[11px] mt-2" style={{ color: "var(--rahi-soft)" }}>
              Have a Mailgun bounce / unsubscribe export instead? Use <a href="/sync" className="underline" style={{ color: "var(--rahi-red)" }}>Sync events</a> &mdash; or just drop it here, we&apos;ll auto-route it.
            </div>
          </>
        )}
      </div>

      {uploadResult && !uploadResult.error && uploadResult.auto_synced && (
        <div className="mb-6 p-5 rounded-md border" style={{ background:"#FFF7ED", borderColor:"#FDBA74", color:"#9A3412" }} data-testid="auto-sync-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={18} style={{ color:"#C2410C", marginTop:2 }}/>
            <div className="flex-1">
              <div className="font-medium mb-1">Auto-routed to Sync events</div>
              <div className="text-sm mb-3">{uploadResult.message}</div>
              <div className="grid grid-cols-4 gap-3 text-xs" style={{ color:"#7C2D12" }}>
                <div><div className="font-mono text-lg font-bold">{uploadResult.total_rows?.toLocaleString("en-IN")}</div>rows in CSV</div>
                <div><div className="font-mono text-lg font-bold">{uploadResult.newly_marked?.toLocaleString("en-IN")}</div>newly marked {uploadResult.kind}</div>
                <div><div className="font-mono text-lg font-bold">{uploadResult.already_marked?.toLocaleString("en-IN")}</div>already marked</div>
                <div><div className="font-mono text-lg font-bold">{uploadResult.not_in_list?.toLocaleString("en-IN")}</div>not in your list</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {uploadResult && !uploadResult.error && !uploadResult.auto_synced && (
        <div className="text-sm mb-6 p-4 rounded-md flex items-center gap-3" style={{ background:"#ECFDF5", color:"#047857" }} data-testid="upload-result">
          <CheckCircle2 size={16}/>
          {uploadResult.mode === "remove" ? (
            <span>
              Removed tag(s) <strong>{(uploadResult.removed_tags || []).join(", ")}</strong> from{" "}
              <strong>{uploadResult.untagged}</strong> recipients ·{" "}
              {uploadResult.no_change} unchanged · {uploadResult.not_found} not in your list
            </span>
          ) : (
            <span>
              Imported as <strong>{(uploadResult.groups || ["donors"]).join(", ")}</strong>:{" "}
              <strong>{uploadResult.added}</strong> added,{" "}
              <strong>{uploadResult.updated}</strong> updated,{" "}
              <strong>{uploadResult.skipped}</strong> skipped
            </span>
          )}
        </div>
      )}
      {uploadResult?.error && (
        <div className="text-sm mb-6 p-4 rounded-md" style={{ background:"#FEF2F2", color:"#B91C1C" }}>{uploadResult.error}</div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--rahi-soft)" }}/>
          <input
            value={q} onChange={(e)=>setQ(e.target.value)}
            placeholder="Search by email or name"
            className="input pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1 p-1 rounded-md border" style={{ borderColor:"var(--rahi-border)", background:"#fff" }}>
          {["all","active","unsubscribed","bounced"].map((f) => (
            <button
              key={f} onClick={()=>setFilter(f)}
              className={`px-3 py-1 rounded text-xs capitalize ${filter===f ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}
              data-testid={`filter-${f}`}
            >{f}</button>
          ))}
        </div>
      </div>

      {/* Group pills */}
      {groups.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="group-filters">
          <span className="text-[11px] uppercase tracking-[0.14em] mr-1" style={{ color:"var(--rahi-soft)" }}>Groups</span>
          <button
            onClick={()=>setGroupFilter("")}
            className={`px-3 py-1 rounded-full text-xs border ${groupFilter==="" ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[var(--rahi-border)] text-[#57534E] bg-white hover:bg-[#FBFAF9]"}`}
            data-testid="group-filter-all"
          >
            All <span className="opacity-60 ml-1">{stats?.total?.toLocaleString("en-IN") || 0}</span>
          </button>
          {groups.map((g) => {
            const isMaster = g.virtual === true || g.group === "master";
            return (
              <button
                key={g.group} onClick={()=>setGroupFilter(g.group)}
                className={`px-3 py-1 rounded-full text-xs border flex items-center gap-1.5 ${groupFilter===g.group ? (isMaster ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-[#FF2101] text-white border-[#FF2101]") : (isMaster ? "border-[#1C1917] text-[#1C1917] bg-white hover:bg-[#FBFAF9] font-medium" : "border-[var(--rahi-border)] text-[#57534E] bg-white hover:bg-[#FFF7F5]")}`}
                data-testid={`group-filter-${g.group}`}
                title={isMaster ? "Virtual group: everyone except staff-only records" : g.group}
              >
                {isMaster ? <Star size={11}/> : <Users size={11}/>} {g.group} <span className="opacity-70 ml-1">{g.active.toLocaleString("en-IN")}</span>
              </button>
            );
          })}
          <GroupTools groups={groups} onChanged={load}/>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm" data-testid="recipients-table">
          <thead>
            <tr style={{ background:"#FBFAF9", color:"var(--rahi-muted)" }} className="text-left text-[11px] uppercase tracking-[0.1em]">
              <th className="px-6 py-3 font-medium">Email</th>
              <th className="px-6 py-3 font-medium">Name</th>
              <th className="px-6 py-3 font-medium">Group</th>
              <th className="px-6 py-3 font-medium">Status</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-sm" style={{ color: "var(--rahi-soft)" }}>No recipients yet. Upload a CSV to get started.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor:"var(--rahi-border)" }} data-testid="recipient-row">
                <td className="px-6 py-3 font-mono text-xs">{r.email}</td>
                <td className="px-6 py-3 text-sm">{r.name || <span style={{ color:"var(--rahi-soft)" }}>—</span>}</td>
                <td className="px-6 py-3 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {((r.groups && r.groups.length > 0) ? r.groups : [r.group || "donors"]).map(g => (
                      <span key={g} className="px-2 py-0.5 rounded-full" style={{ background:"#FBFAF9", color:"var(--rahi-muted)" }}>{g}</span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-3">
                  {r.unsubscribed ? <span className="badge badge-draft">Unsubscribed</span> :
                   r.bounced ? <span className="badge badge-failed">Bounced</span> :
                   r.complained ? <span className="badge badge-failed">Complained</span> :
                   <span className="badge badge-completed">Active</span>}
                </td>
                <td className="px-6 py-3 text-right">
                  <button onClick={()=>removeOne(r.id)} className="text-xs px-2 py-1 rounded hover:bg-[#FEF2F2]" style={{ color:"var(--rahi-red)" }} data-testid="delete-recipient-btn">
                    <Trash2 size={13}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > items.length && (
          <div className="px-6 py-4 text-xs border-t flex items-center justify-between gap-3" style={{ borderColor:"var(--rahi-border)", color:"var(--rahi-soft)" }}>
            <span>Showing {items.length.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}</span>
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="text-xs px-4 py-1.5 rounded-full border hover:bg-[#FBFAF9] disabled:opacity-60 flex items-center gap-2"
              style={{ borderColor:"var(--rahi-border)", color:"var(--rahi-ink)" }}
              data-testid="load-more-btn"
            >
              {loadingMore ? <><Loader2 size={12} className="animate-spin"/> Loading…</> : `Load more (next ${Math.min(PAGE_SIZE, total - items.length).toLocaleString("en-IN")})`}
            </button>
          </div>
        )}
      </div>

      {/* Tag-confirmation modal (mandatory before any CSV upload) */}
      {pendingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(28,25,23,0.6)" }} data-testid="upload-confirm-modal">
          <div className="card max-w-md w-full p-7">
            <div className="w-10 h-[3px] mb-5" style={{ background:"var(--rahi-red)" }}/>
            <h2 className="font-display text-2xl font-black mb-1 tracking-tight">Confirm before upload</h2>
            <p className="text-xs mb-4" style={{ color:"var(--rahi-muted)" }}>
              File: <span className="font-mono">{pendingFile.name}</span>
            </p>

            <div className="text-[11px] uppercase tracking-[0.14em] mb-2" style={{ color:"var(--rahi-muted)" }}>Mode</div>
            <div className="flex gap-1.5 mb-4">
              <button
                onClick={() => setPendingMode("add")}
                className={`px-3 py-1.5 rounded text-xs border ${pendingMode === "add" ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[var(--rahi-border)] text-[#57534E] bg-white"}`}
                data-testid="mode-add"
              >Add tag(s)</button>
              <button
                onClick={() => setPendingMode("remove")}
                className={`px-3 py-1.5 rounded text-xs border ${pendingMode === "remove" ? "bg-[#FF2101] text-white border-[#FF2101]" : "border-[var(--rahi-border)] text-[#57534E] bg-white"}`}
                data-testid="mode-remove"
              >Remove tag(s)</button>
            </div>

            <label className="label">Tags ({pendingMode === "add" ? "applied to every email in the CSV" : "removed from every email in the CSV"})</label>
            <input
              value={pendingTags}
              onChange={(e) => setPendingTags(e.target.value)}
              list="known-groups"
              autoFocus
              className="input mb-2"
              placeholder="e.g. staff   or   donors,volunteers"
              data-testid="pending-tags-input"
            />
            <p className="hint mb-5">
              Pick the existing group from the dropdown or type a new one. Comma-separate multiple tags.
            </p>

            {pendingMode === "add" ? (
              <p className="text-xs px-3 py-2 rounded mb-4" style={{ background:"#FFF7F5", color:"#7C2D12" }}>
                ✓ Existing emails will gain these tags; their current tags and bounce/unsub state are preserved.<br/>
                ✓ New emails will be added with these tags.
              </p>
            ) : (
              <p className="text-xs px-3 py-2 rounded mb-4" style={{ background:"#FEF2F2", color:"#7F1D1D" }}>
                ✓ Listed tags will be PULLED off matching emails. Recipients themselves are NOT deleted.<br/>
                ✓ Use this to fix a mis-tagged upload.
              </p>
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={() => setPendingFile(null)} className="btn btn-ghost" data-testid="upload-cancel-btn">Cancel</button>
              <button
                onClick={doUpload}
                disabled={!pendingTags.trim()}
                className="btn btn-primary"
                data-testid="upload-confirm-btn"
              >
                {pendingMode === "add" ? "Add tag & upload" : "Remove tag from listed emails"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.12em] mb-2" style={{ color: "var(--rahi-soft)" }}>{label}</div>
      <div className="font-display text-3xl font-black tracking-tight" style={{ color: accent ? "var(--rahi-red)" : "var(--rahi-ink)" }}>
        {value.toLocaleString("en-IN")}
      </div>
    </div>
  );
}


function GroupTools({ groups, onChanged }) {
  const [open, setOpen] = useState(false);
  const [oldName, setOldName] = useState("");
  const [newName, setNewName] = useState("");
  const [msg, setMsg] = useState("");

  // Strip-tag state
  const [stripOpen, setStripOpen] = useState(false);
  const [stripFrom, setStripFrom] = useState("");
  const [stripTag, setStripTag] = useState("");
  const [stripMsg, setStripMsg] = useState("");
  const [stripping, setStripping] = useState(false);

  // Delete-group state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [deleteMsg, setDeleteMsg] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Only real groups (not the virtual `master`) should appear in these pickers
  const realGroups = groups.filter(g => !(g.virtual === true || g.group === "master"));

  const rename = async () => {
    if (!oldName || !newName) { setMsg("Both names required"); return; }
    try {
      const { data } = await api.post("/recipients/groups/rename", { old_name: oldName, new_name: newName });
      setMsg(`Renamed/merged ${data.moved} recipients`);
      setOldName(""); setNewName("");
      onChanged && onChanged();
    } catch (e) {
      setMsg(formatErr(e.response?.data?.detail));
    }
  };

  const strip = async () => {
    if (!stripFrom || !stripTag) { setStripMsg("Pick both group and tag"); return; }
    if (stripFrom === stripTag) { setStripMsg("Group and tag must differ"); return; }
    if (!window.confirm(
      `Remove tag "${stripTag}" from every recipient currently in group "${stripFrom}"?\n\n` +
      `Recipients will NOT be deleted — only the "${stripTag}" label will be pulled from them.`
    )) return;
    setStripping(true); setStripMsg("");
    try {
      const { data } = await api.post("/recipients/groups/strip", { from_group: stripFrom, remove_tag: stripTag });
      setStripMsg(`✓ Stripped "${data.remove_tag}" from ${data.stripped.toLocaleString("en-IN")} recipients in "${data.from_group}"`);
      onChanged && onChanged();
    } catch (e) {
      setStripMsg(formatErr(e.response?.data?.detail));
    } finally {
      setStripping(false);
    }
  };

  const deleteGroup = async () => {
    if (!deleteName) { setDeleteMsg("Pick a group"); return; }
    if (!window.confirm(
      `Delete group "${deleteName}"?\n\n` +
      `This removes the "${deleteName}" tag from every recipient. Recipients themselves are NOT deleted.\n\n` +
      `Recipients that have no other tag after this will show as untagged.`
    )) return;
    setDeleting(true); setDeleteMsg("");
    try {
      const { data } = await api.post("/recipients/groups/delete", { name: deleteName });
      setDeleteMsg(`✓ Deleted group "${deleteName}" — untagged ${data.untagged.toLocaleString("en-IN")} recipients.`);
      setDeleteName("");
      onChanged && onChanged();
    } catch (e) {
      setDeleteMsg(formatErr(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <button
        onClick={()=>setOpen(true)}
        className="text-xs px-3 py-1 rounded-full border ml-2"
        style={{ borderColor:"var(--rahi-border)", color:"var(--rahi-muted)" }}
        data-testid="rename-group-btn"
      >
        Rename / merge
      </button>
      <button
        onClick={()=>setStripOpen(true)}
        className="text-xs px-3 py-1 rounded-full border flex items-center gap-1"
        style={{ borderColor:"var(--rahi-border)", color:"var(--rahi-muted)" }}
        data-testid="strip-tag-btn"
      >
        <Scissors size={11}/> Strip tag from group
      </button>
      <button
        onClick={()=>setDeleteOpen(true)}
        className="text-xs px-3 py-1 rounded-full border flex items-center gap-1"
        style={{ borderColor:"#DC2626", color:"#DC2626" }}
        data-testid="delete-group-btn"
      >
        Delete group
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(28,25,23,0.6)" }} data-testid="rename-group-modal">
          <div className="card max-w-md w-full p-7">
            <h3 className="font-display text-xl font-black mb-1 tracking-tight">Rename or merge a group</h3>
            <p className="text-xs mb-5" style={{ color:"var(--rahi-muted)" }}>
              Use this to fix typos (e.g. <span className="font-mono">donor</span> &rarr; <span className="font-mono">donors</span>)
              or merge two groups (rename A to an existing name B). Recipient tags are deduplicated automatically.
            </p>
            <label className="label">Existing group name</label>
            <select className="input mb-3" value={oldName} onChange={(e)=>setOldName(e.target.value)} data-testid="rename-from">
              <option value="">— select —</option>
              {realGroups.map(g => <option key={g.group} value={g.group}>{g.group} ({g.total})</option>)}
            </select>
            <label className="label">New name (or existing group to merge into)</label>
            <input className="input mb-4" value={newName} onChange={(e)=>setNewName(e.target.value)} list="known-groups" placeholder="donors" data-testid="rename-to"/>
            {msg && <p className="text-xs mb-3" style={{ color: msg.includes("Renamed") ? "#047857" : "#B91C1C" }}>{msg}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{setOpen(false); setMsg("");}} className="btn btn-ghost" data-testid="rename-cancel-btn">Close</button>
              <button onClick={rename} className="btn btn-primary" disabled={!oldName || !newName} data-testid="rename-confirm-btn">Apply</button>
            </div>
          </div>
        </div>
      )}
      {stripOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(28,25,23,0.6)" }} data-testid="strip-tag-modal">
          <div className="card max-w-md w-full p-7">
            <h3 className="font-display text-xl font-black mb-1 tracking-tight">Strip a tag from a group</h3>
            <p className="text-xs mb-5" style={{ color:"var(--rahi-muted)" }}>
              Removes one tag from every recipient in the chosen group. Useful when an upload
              accidentally applied a stale tag (e.g. volunteers mis-tagged as donors).
              Recipients themselves are not deleted.
            </p>
            <label className="label">From group</label>
            <select className="input mb-3" value={stripFrom} onChange={(e)=>setStripFrom(e.target.value)} data-testid="strip-from">
              <option value="">— select group —</option>
              {realGroups.map(g => <option key={g.group} value={g.group}>{g.group} ({g.total.toLocaleString("en-IN")})</option>)}
            </select>
            <label className="label">Remove this tag</label>
            <select className="input mb-4" value={stripTag} onChange={(e)=>setStripTag(e.target.value)} data-testid="strip-tag-select">
              <option value="">— select tag to remove —</option>
              {realGroups.filter(g => g.group !== stripFrom).map(g => <option key={g.group} value={g.group}>{g.group}</option>)}
            </select>
            {stripMsg && <p className="text-xs mb-3" style={{ color: stripMsg.startsWith("✓") ? "#047857" : "#B91C1C" }}>{stripMsg}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{setStripOpen(false); setStripMsg(""); setStripFrom(""); setStripTag("");}} className="btn btn-ghost" data-testid="strip-cancel-btn">Close</button>
              <button onClick={strip} className="btn btn-primary" disabled={!stripFrom || !stripTag || stripping} data-testid="strip-confirm-btn">
                {stripping ? "Stripping…" : "Strip tag"}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(28,25,23,0.6)" }} data-testid="delete-group-modal">
          <div className="card max-w-md w-full p-7">
            <h3 className="font-display text-xl font-black mb-1 tracking-tight" style={{ color:"#DC2626" }}>Delete a group</h3>
            <p className="text-xs mb-5" style={{ color:"var(--rahi-muted)" }}>
              Removes the selected tag from every recipient. Useful for cleaning up empty stale groups
              (e.g. old <span className="font-mono">donors</span>, <span className="font-mono">newsletter</span>).
              Recipients themselves stay in the database and keep their other tags.
            </p>
            <label className="label">Group to delete</label>
            <select className="input mb-4" value={deleteName} onChange={(e)=>setDeleteName(e.target.value)} data-testid="delete-select">
              <option value="">— select —</option>
              {realGroups.map(g => <option key={g.group} value={g.group}>{g.group} ({g.total.toLocaleString("en-IN")})</option>)}
            </select>
            {deleteMsg && <p className="text-xs mb-3" style={{ color: deleteMsg.startsWith("✓") ? "#047857" : "#B91C1C" }}>{deleteMsg}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={()=>{setDeleteOpen(false); setDeleteMsg(""); setDeleteName("");}} className="btn btn-ghost" data-testid="delete-cancel-btn">Close</button>
              <button onClick={deleteGroup} className="btn" style={{ background:"#DC2626", color:"#fff" }} disabled={!deleteName || deleting} data-testid="delete-confirm-btn">
                {deleting ? "Deleting…" : "Delete group"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
